import React, { createContext, useContext, useEffect, useState } from "react";
import {
  getConfig, getMe, signInDemo, signInWith, signInEmail, signOut,
  type Me, type AuthConfig,
} from "../lib/authClient";

interface AuthCtx {
  me: Me | null;
  config: AuthConfig | null;
  loading: boolean;
  refresh: () => Promise<void>;
  demo: () => Promise<void>;
  sso: (provider: "google" | "microsoft") => Promise<void>;
  email: (address: string) => Promise<{ sent: boolean; devLink?: string }>;
  logout: () => Promise<void>;
  setPlan: (plan: Me["plan"]) => void;
}

const Ctx = createContext<AuthCtx | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [me, setMe] = useState<Me | null>(null);
  const [config, setConfig] = useState<AuthConfig | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = async () => { setMe(await getMe()); };

  useEffect(() => {
    let alive = true;
    (async () => {
      const [cfg, m] = await Promise.all([getConfig(), getMe()]);
      if (!alive) return;
      setConfig(cfg);
      setMe(m);
      setLoading(false);
    })();
    return () => { alive = false; };
  }, []);

  const value: AuthCtx = {
    me, config, loading, refresh,
    demo: async () => { setMe(await signInDemo()); },
    sso: async (provider) => { const m = await signInWith(provider); if (m) setMe(m); },
    email: async (address) => { const r = await signInEmail(address); await refresh(); return r; },
    logout: async () => { await signOut(); setMe(null); },
    setPlan: (plan) => setMe((prev) => (prev ? { ...prev, plan } : prev)),
  };

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthCtx {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
